import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  AlertColor,
  AlertTitle,
  Box,
  Collapse,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

// ==========================================
// 1. TIPOS Y DEFINICIONES
// ==========================================

type HorizontalPos = 'left' | 'center' | 'right'
type VerticalPos = 'top' | 'bottom'
export type ToastPosition = `${HorizontalPos}_${VerticalPos}`

export interface ToastOptions {
  position?: ToastPosition
  /**
   * Milisegundos hasta el cierre automático. `null` = no se cierra solo (solo con la X o `dismiss`).
   * Si no se indica, depende de la severidad (ver `DURACION_POR_SEVERIDAD`).
   */
  duration?: number | null
  title?: string | React.ReactNode
  action?: React.ReactNode
  /**
   * Id propio del toast. Si ya hay uno con ese id, se actualiza en lugar de crear otro
   * (p. ej. "Guardando pedido…" → "Pedido guardado"). Útil también para cerrarlo con `dismiss(id)`.
   */
  id?: string
}

export interface ToastProviderProps {
  children: React.ReactNode
  defaultOptions?: ToastOptions
  /**
   * Cantidad máxima de toasts visibles a la vez (por defecto 3).
   * Al superarla se cierran primero los de menor prioridad (info/success) y más antiguos.
   */
  maxToasts?: number
  /** Máximo visible en móvil, donde el espacio es menor (por defecto 2) */
  maxToastsMobile?: number
}

type ShowToastOptions = ToastOptions & { severity?: AlertColor }

export interface ToastApi {
  /** Muestra un toast y devuelve su id (sirve para cerrarlo con `dismiss(id)`) */
  (message: React.ReactNode, options?: ShowToastOptions): string
  info: (message: React.ReactNode, options?: ToastOptions) => string
  success: (message: React.ReactNode, options?: ToastOptions) => string
  warning: (message: React.ReactNode, options?: ToastOptions) => string
  error: (message: React.ReactNode, options?: ToastOptions) => string
  /** Cierra el toast indicado; sin id cierra todos */
  dismiss: (id?: string) => void
}

export interface ToastContextType {
  toast: ToastApi
}

interface ToastItem {
  id: string
  message: React.ReactNode
  severity: AlertColor
  position: ToastPosition
  duration: number | null
  title?: string | React.ReactNode
  action?: React.ReactNode
  /** Veces que se pidió el mismo toast mientras estaba abierto (se muestra ×N) */
  count: number
  /** Se incrementa al repetirse el toast para reiniciar su temporizador */
  resetKey: number
  /** false mientras corre la animación de salida; al terminar se quita de la lista */
  open: boolean
}

// ==========================================
// 2. CONTEXTO Y HOOK PERSONALIZADO
// ==========================================

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast debe usarse dentro de un ToastProvider')
  }
  return context
}

// ==========================================
// 3. UTILIDADES (fuera del componente)
// ==========================================

/**
 * Id nativo y único por sesión. No se usa crypto.randomUUID porque solo existe en contextos
 * seguros (https/localhost) y los POS suelen abrirse por http en la red local.
 */
let toastSeq = 0
const nextToastId = () => `toast-${Date.now().toString(36)}-${(toastSeq++).toString(36)}`

/**
 * Arma la función `toast` con sus atajos. Vive fuera del componente: el React Compiler
 * no permite modificar (asignar propiedades a) una función creada durante el render.
 */
const createToastApi = (
  show: (message: React.ReactNode, options?: ShowToastOptions) => string,
  dismiss: (id?: string) => void,
): ToastApi =>
  Object.assign((message: React.ReactNode, options?: ShowToastOptions) => show(message, options), {
    info: (message: React.ReactNode, options?: ToastOptions) =>
      show(message, { ...options, severity: 'info' }),
    success: (message: React.ReactNode, options?: ToastOptions) =>
      show(message, { ...options, severity: 'success' }),
    warning: (message: React.ReactNode, options?: ToastOptions) =>
      show(message, { ...options, severity: 'warning' }),
    error: (message: React.ReactNode, options?: ToastOptions) =>
      show(message, { ...options, severity: 'error' }),
    dismiss,
  })

const parsePosition = (pos: ToastPosition) => {
  const [horizontal, vertical] = pos.split('_') as [HorizontalPos, VerticalPos]
  return { horizontal, vertical }
}

const slideDirection = (pos: ToastPosition, isMobile: boolean): 'left' | 'right' | 'up' | 'down' => {
  if (isMobile) return 'up'
  const { horizontal, vertical } = parsePosition(pos)
  if (horizontal === 'right') return 'left'
  if (horizontal === 'left') return 'right'
  return vertical === 'top' ? 'down' : 'up'
}

/**
 * Store mínimo de la lista de toasts. Se actualiza de forma síncrona (permite deduplicar y devolver
 * el id correcto aunque se llame a `toast` varias veces en el mismo evento) y React lo lee con
 * `useSyncExternalStore`. Se usa en lugar de un ref porque el React Compiler no permite que
 * funciones que leen refs se pasen a otras durante el render.
 */
const createToastStore = () => {
  let lista: ToastItem[] = []
  const listeners = new Set<() => void>()
  return {
    get: () => lista,
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    update: (updater: (actuales: ToastItem[]) => ToastItem[]) => {
      const next = updater(lista)
      if (next === lista) return
      lista = next
      listeners.forEach((l) => l())
    },
  }
}

const TRANSITION_MS = 225
/** Píxeles de arrastre horizontal para cerrar un toast deslizándolo (táctil) */
const SWIPE_CIERRE_PX = 80

/** Duración por defecto según severidad (ms) */
const DURACION_POR_SEVERIDAD: Record<AlertColor, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 8000,
}
/** Un toast con acción (p. ej. "Deshacer") necesita tiempo para reaccionar */
const DURACION_MINIMA_CON_ACCION = 8000

/**
 * Prioridad para decidir qué cerrar al superar `maxToasts`: se cierra primero el de menor
 * prioridad y, a igual prioridad, el más antiguo. Los persistentes (`duration: null`) se conservan.
 */
const prioridad = (t: ToastItem) =>
  (t.duration === null ? 10 : 0) + (t.severity === 'error' ? 2 : t.severity === 'warning' ? 1 : 0)

/** Solo se deduplican contenidos comparables (texto); un ReactNode no se puede comparar con seguridad */
const esComparable = (v: React.ReactNode) => v === undefined || typeof v === 'string' || typeof v === 'number'

// ==========================================
// 4. UN TOAST (temporizador propio)
// ==========================================

interface ToastViewProps {
  item: ToastItem
  isMobile: boolean
  /** Pausado por el grupo (mouse sobre cualquier toast de la misma posición) o pestaña oculta */
  paused: boolean
  onHover: (pos: ToastPosition, hovering: boolean) => void
  onClose: (id: string) => void
  onExited: (id: string) => void
}

const ToastView = React.memo(({ item, isMobile, paused, onHover, onClose, onExited }: ToastViewProps) => {
  const { id, duration, open, resetKey } = item
  // Tiempo restante: se descuenta lo transcurrido cada vez que se pausa
  const remainingRef = useRef<number | null>(duration)
  const resetKeyRef = useRef(resetKey)

  useEffect(() => {
    // El mismo toast se volvió a pedir: arranca de nuevo con la duración completa
    if (resetKeyRef.current !== resetKey) {
      resetKeyRef.current = resetKey
      remainingRef.current = duration
    }
    if (!open || paused || remainingRef.current === null) return
    const startedAt = Date.now()
    const timeout = window.setTimeout(() => onClose(id), Math.max(remainingRef.current, 0))
    return () => {
      window.clearTimeout(timeout)
      if (remainingRef.current !== null) remainingRef.current -= Date.now() - startedAt
    }
  }, [id, open, paused, duration, resetKey, onClose])

  const handleClose = () => onClose(id)
  const hoverPos: ToastPosition = isMobile ? 'center_bottom' : item.position

  // Si se desmonta con el mouse encima (p. ej. al cerrarlo con la X) no llega `mouseleave`:
  // se libera la pausa del grupo para que los demás sigan corriendo
  const hoveringRef = useRef<ToastPosition | null>(null)
  const setHover = (hovering: boolean) => {
    hoveringRef.current = hovering ? hoverPos : null
    onHover(hoverPos, hovering)
  }
  useEffect(
    () => () => {
      if (hoveringRef.current) onHover(hoveringRef.current, false)
    },
    [onHover],
  )

  // Deslizar para cerrar (táctil). Mientras se toca, el grupo queda pausado.
  const [dragX, setDragX] = useState(0)
  const [dragging, setDragging] = useState(false)
  const touchStartXRef = useRef(0)
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX
    setDragging(true)
    setHover(true)
  }
  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragging) setDragX(e.touches[0].clientX - touchStartXRef.current)
  }
  const handleTouchEnd = () => {
    setDragging(false)
    setHover(false)
    if (Math.abs(dragX) >= SWIPE_CIERRE_PX) {
      // Sale volando hacia el lado del gesto mientras el Collapse lo encoge
      setDragX(Math.sign(dragX) * window.innerWidth)
      handleClose()
    } else {
      setDragX(0)
    }
  }

  return (
    <Collapse in={open} appear timeout={TRANSITION_MS} onExited={() => onExited(id)} unmountOnExit>
      <Slide in={open} appear direction={slideDirection(item.position, isMobile)} timeout={TRANSITION_MS}>
        <Box
          sx={{ pb: 1, pointerEvents: 'auto' }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <Box
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
            style={{
              transform: dragX ? `translateX(${dragX}px)` : undefined,
              opacity: dragX ? Math.max(0, 1 - Math.abs(dragX) / (SWIPE_CIERRE_PX * 2.5)) : undefined,
              transition: dragging
                ? 'none'
                : `transform ${TRANSITION_MS}ms ease, opacity ${TRANSITION_MS}ms ease`,
            }}
            sx={{ touchAction: 'pan-y' }}
          >
            <Alert
              onClose={handleClose}
              severity={item.severity}
              variant="filled"
              elevation={8}
              role={item.severity === 'error' ? 'alert' : 'status'}
              action={
                item.action ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {item.action}
                    <IconButton size="small" aria-label="cerrar" color="inherit" onClick={handleClose}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : undefined
              }
              sx={{
                width: '100%',
                minWidth: { xs: '260px', sm: '300px' },
                maxWidth: { xs: '100%', sm: '420px' },
                wordBreak: 'break-word',
                borderRadius: '8px',
                color: '#FFFFFF !important',
                fontWeight: 500,
                boxShadow: (th) => th.shadows[8],
                '& .MuiAlert-icon': { color: '#FFFFFF !important', fontSize: '22px' },
                '& .MuiAlert-action': { color: '#FFFFFF !important' },
                '& .MuiAlert-message': { color: '#FFFFFF !important' },
              }}
            >
              {item.title && (
                <AlertTitle
                  sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.25, color: '#FFFFFF !important' }}
                >
                  {item.title}
                </AlertTitle>
              )}
              {item.message}
              {item.count > 1 && (
                <Box component="span" sx={{ ml: 1, fontWeight: 700, opacity: 0.85 }}>
                  ×{item.count}
                </Box>
              )}
            </Alert>
          </Box>
        </Box>
      </Slide>
    </Collapse>
  )
})
ToastView.displayName = 'ToastView'

// ==========================================
// 5. TOAST PROVIDER (el motor del sistema)
// ==========================================

export const ToastProvider = ({
  children,
  defaultOptions,
  maxToasts = 3,
  maxToastsMobile = 2,
}: ToastProviderProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  // Una instancia del store por provider (inicialización perezosa, estable entre renders)
  const [store] = useState(createToastStore)
  const toasts = useSyncExternalStore(store.subscribe, store.get, store.get)
  const commit = store.update
  // Posición cuyo grupo tiene el mouse encima (pausa todos sus toasts)
  const [hoveredPos, setHoveredPos] = useState<ToastPosition | null>(null)
  // Con la pestaña oculta (el cajero cambió de ventana) los toasts no corren
  const [pageHidden, setPageHidden] = useState(() => typeof document !== 'undefined' && document.hidden)

  useEffect(() => {
    const onVisibility = () => setPageHidden(document.hidden)
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  // Valores por defecto globales
  const fallbackPosition = defaultOptions?.position || 'right_bottom'
  /** `undefined` = usar la duración por severidad */
  const globalDuration = defaultOptions?.duration
  const limite = Math.max(1, isMobile ? Math.min(maxToasts, maxToastsMobile) : maxToasts)

  const show = useCallback(
    (message: React.ReactNode, options?: ShowToastOptions) => {
      const severity = options?.severity || 'info'
      const position = options?.position || fallbackPosition
      const title = options?.title
      const action = options?.action
      let duration: number | null
      if (options?.duration !== undefined) duration = options.duration
      else if (globalDuration !== undefined) duration = globalDuration
      else {
        duration = DURACION_POR_SEVERIDAD[severity]
        if (action) duration = Math.max(duration, DURACION_MINIMA_CON_ACCION)
      }

      const idPropio = options?.id
      const id = idPropio ?? nextToastId()
      let idResultado = id
      // Deduplicación (solo sin id propio): si ya hay uno abierto idéntico, se reutiliza
      // (contador + reinicio del tiempo)
      const dedupe = !idPropio && !action && esComparable(message) && esComparable(title as React.ReactNode)

      commit((actuales) => {
        let lista: ToastItem[]
        const conMismoId = idPropio ? actuales.find((t) => t.id === idPropio) : undefined
        const duplicado = dedupe
          ? actuales.find(
              (t) =>
                t.open &&
                !t.action &&
                t.severity === severity &&
                t.position === position &&
                t.message === message &&
                t.title === title,
            )
          : undefined

        if (conMismoId) {
          // Actualización por id: se reemplaza el contenido (y se reabre si estaba saliendo)
          lista = actuales.map((t) =>
            t.id === idPropio
              ? {
                  ...t,
                  message,
                  severity,
                  position,
                  duration,
                  title,
                  action,
                  count: 1,
                  resetKey: t.resetKey + 1,
                  open: true,
                }
              : t,
          )
          if (conMismoId.open) return lista
        } else if (duplicado) {
          idResultado = duplicado.id
          return actuales.map((t) =>
            t.id === duplicado.id ? { ...t, count: t.count + 1, resetKey: t.resetKey + 1, duration } : t,
          )
        } else {
          lista = [
            ...actuales,
            { id, message, severity, position, duration, title, action, count: 1, resetKey: 0, open: true },
          ]
        }

        const abiertos = lista.filter((t) => t.open && t.id !== id)
        const sobrantes = abiertos.length + 1 - limite
        if (sobrantes <= 0) return lista

        // Se cierran los de menor prioridad; a igual prioridad, los más antiguos (sort estable)
        const aCerrar = new Set(
          [...abiertos]
            .sort((x, y) => prioridad(x) - prioridad(y))
            .slice(0, sobrantes)
            .map((t) => t.id),
        )
        return lista.map((t) => (aCerrar.has(t.id) ? { ...t, open: false } : t))
      })
      return idResultado
    },
    [fallbackPosition, globalDuration, limite, commit],
  )

  const dismiss = useCallback(
    (id?: string) => {
      commit((actuales) =>
        actuales.some((t) => t.open && (id === undefined || t.id === id))
          ? actuales.map((t) => (t.open && (id === undefined || t.id === id) ? { ...t, open: false } : t))
          : actuales,
      )
    },
    [commit],
  )

  const remove = useCallback(
    (id: string) => {
      commit((actuales) =>
        actuales.some((t) => t.id === id) ? actuales.filter((t) => t.id !== id) : actuales,
      )
    },
    [commit],
  )

  const handleHover = useCallback((pos: ToastPosition, hovering: boolean) => {
    setHoveredPos((actual) => (hovering ? pos : actual === pos ? null : actual))
  }, [])

  const toast = useMemo(() => createToastApi(show, dismiss), [show, dismiss])
  const value = useMemo<ToastContextType>(() => ({ toast }), [toast])

  // Agrupación por posición (en móvil todo va abajo al centro)
  const grupos = useMemo(() => {
    const mapa = new Map<ToastPosition, ToastItem[]>()
    toasts.forEach((t) => {
      const pos: ToastPosition = isMobile ? 'center_bottom' : t.position
      mapa.set(pos, [...(mapa.get(pos) ?? []), t])
    })
    return [...mapa.entries()]
  }, [toasts, isMobile])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {grupos.map(([pos, items]) => {
        const { horizontal, vertical } = parsePosition(pos)
        return (
          <Box
            key={pos}
            aria-live="polite"
            sx={{
              position: 'fixed',
              zIndex: 100000,
              display: 'flex',
              // Arriba, el más reciente queda pegado al borde (orden invertido)
              flexDirection: !isMobile && vertical === 'top' ? 'column-reverse' : 'column',
              pointerEvents: 'none',
              ...(isMobile
                ? {
                    left: 16,
                    right: 16,
                    bottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
                  }
                : {
                    [vertical]: 24,
                    ...(horizontal === 'center'
                      ? { left: '50%', transform: 'translateX(-50%)' }
                      : { [horizontal]: 24 }),
                    alignItems:
                      horizontal === 'right' ? 'flex-end' : horizontal === 'left' ? 'flex-start' : 'center',
                  }),
            }}
          >
            {items.map((item) => (
              <ToastView
                key={item.id}
                item={item}
                isMobile={isMobile}
                paused={pageHidden || hoveredPos === pos}
                onHover={handleHover}
                onClose={dismiss}
                onExited={remove}
              />
            ))}
          </Box>
        )
      })}
    </ToastContext.Provider>
  )
}
